import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import type {
  FeedbackEntry,
  FeedbackType,
  StrategyDetail,
  StrategySummary,
} from "@/lib/types";
import { Card, CardTitle } from "@/components/Card";
import FeedbackProposalsCard from "@/components/FeedbackProposalsCard";
import { formatDateTime } from "@/lib/format";
import { cn } from "@/lib/cn";
import { useAuth } from "@/lib/auth";

const TYPES: FeedbackType[] = ["MISTAKE", "BAD_BEHAVIOR", "GOOD_BEHAVIOR", "PATTERN"];

const TYPE_LABEL: Record<FeedbackType, string> = {
  MISTAKE: "Mistake",
  BAD_BEHAVIOR: "Bad pattern",
  GOOD_BEHAVIOR: "Good pattern",
  PATTERN: "Reusable pattern",
};

const TYPE_TONE: Record<FeedbackType, string> = {
  MISTAKE: "bg-red-50 text-red-700 border-red-200",
  BAD_BEHAVIOR: "bg-amber-50 text-amber-800 border-amber-200",
  GOOD_BEHAVIOR: "bg-emerald-50 text-emerald-700 border-emerald-200",
  PATTERN: "bg-sky-50 text-sky-700 border-sky-200",
};

interface FormState {
  type: FeedbackType;
  phaseId: string;        // "" = all phases
  relatedEmailId: string; // optional UUID
  scenario: string;
  exampleEmail: string;
  correction: string;
}

const EMPTY_FORM: FormState = {
  type: "MISTAKE",
  phaseId: "",
  relatedEmailId: "",
  scenario: "",
  exampleEmail: "",
  correction: "",
};

export default function TrainingPage() {
  const { user } = useAuth();
  const canEdit = (user?.roles ?? []).some((r) => r === "ADMIN" || r === "OPERATOR");
  // Applying a proposal rewrites the strategy configuration, which is admin-only
  // everywhere else in the app; reviewing one is not.
  const canDecide = (user?.roles ?? []).some((r) => r === "ADMIN");

  const strategies = useQuery({
    queryKey: ["strategies"],
    queryFn: () => api.get<StrategySummary[]>("/api/v1/strategies"),
  });

  const [strategyId, setStrategyId] = useState<string | null>(null);
  const activeStrategyId = strategyId ?? strategies.data?.[0]?.id ?? null;
  const activeStrategySlug = useMemo(
    () => strategies.data?.find((s) => s.id === activeStrategyId)?.slug ?? null,
    [strategies.data, activeStrategyId],
  );

  const detail = useQuery({
    queryKey: ["strategy-detail", activeStrategySlug],
    queryFn: () => api.get<StrategyDetail>(`/api/v1/strategies/${activeStrategySlug}`),
    enabled: !!activeStrategySlug,
  });

  const [activeOnly, setActiveOnly] = useState(false);
  const list = useQuery({
    queryKey: ["feedback", activeStrategyId, activeOnly],
    queryFn: () =>
      api.get<FeedbackEntry[]>(
        `/api/v1/strategies/${activeStrategyId}/feedback?activeOnly=${activeOnly}`,
      ),
    enabled: !!activeStrategyId,
  });

  if (strategies.isLoading) {
    return <div className="text-sm text-zinc-500">Loading…</div>;
  }
  if (strategies.error || !strategies.data || strategies.data.length === 0) {
    return (
      <Card>
        <CardTitle>Agent Training</CardTitle>
        <p className="text-sm text-[var(--fg-muted)]">
          No strategies configured yet. Upload a strategy YAML before adding training notes.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Agent Training</h1>
          <p className="text-sm text-[var(--fg-muted)]">
            Curated guidance the agent retrieves at email-generation time. Higher-relevance entries
            (matched by vector similarity to the current thread) are injected into the prompt.
          </p>
        </div>
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <label className="text-sm">
          <div className="text-xs text-[var(--fg-muted)] mb-1">Strategy</div>
          <select
            value={activeStrategyId ?? ""}
            onChange={(e) => setStrategyId(e.target.value)}
            className="rounded-md border border-[var(--border)] bg-white px-3 py-1.5 text-sm"
          >
            {strategies.data.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.slug})
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm flex items-center gap-2 pb-1.5">
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => setActiveOnly(e.target.checked)}
          />
          <span>Show only active</span>
        </label>
      </div>

      {canEdit && activeStrategyId ? (
        <FeedbackProposalsCard
          strategyId={activeStrategyId}
          phases={detail.data?.phases ?? []}
          canDecide={canDecide}
        />
      ) : null}

      {canEdit && activeStrategyId ? (
        <NewFeedbackCard
          strategyId={activeStrategyId}
          phases={detail.data?.phases ?? []}
        />
      ) : !canEdit ? (
        <Card>
          <p className="text-sm text-[var(--fg-muted)]">
            Read-only — only ADMIN or OPERATOR roles can add or edit feedback.
          </p>
        </Card>
      ) : null}

      <div className="space-y-3">
        {list.isLoading ? (
          <Card><p className="text-sm text-zinc-500">Loading…</p></Card>
        ) : list.error ? (
          <Card><p className="text-sm text-[var(--danger)]">Failed to load feedback.</p></Card>
        ) : list.data && list.data.length > 0 ? (
          list.data.map((f) => (
            <FeedbackCard
              key={f.id}
              entry={f}
              canEdit={canEdit}
              phaseLabel={phaseLabel(detail.data, f.phaseId)}
              strategyId={activeStrategyId!}
            />
          ))
        ) : (
          <Card><p className="text-sm text-[var(--fg-muted)]">No training notes yet.</p></Card>
        )}
      </div>
    </div>
  );
}

function phaseLabel(detail: StrategyDetail | undefined, phaseId: string | null): string {
  if (!phaseId) return "all phases";
  const ph = detail?.phases.find((p) => p.id === phaseId);
  return ph ? `${ph.name} (${ph.slug})` : "—";
}

function NewFeedbackCard({
  strategyId,
  phases,
}: {
  strategyId: string;
  phases: { id: string; name: string; slug: string }[];
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: (input: FormState) =>
      api.post<FeedbackEntry>(`/api/v1/strategies/${strategyId}/feedback`, {
        type: input.type,
        phaseId: input.phaseId || null,
        relatedEmailId: input.relatedEmailId || null,
        scenario: input.scenario,
        exampleEmail: input.exampleEmail || null,
        correction: input.correction,
      }),
    onSuccess: () => {
      setForm(EMPTY_FORM);
      setError(null);
      qc.invalidateQueries({ queryKey: ["feedback", strategyId] });
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? `Save failed (${e.status})` : "Save failed.");
    },
  });

  const submitting = create.isPending;
  const submittable = form.scenario.trim().length > 0 && form.correction.trim().length > 0;

  return (
    <Card>
      <CardTitle>Add a training note</CardTitle>
      <form
        className="grid gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (submittable) create.mutate(form);
        }}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Select
            label="Type"
            value={form.type}
            onChange={(v) => setForm({ ...form, type: v as FeedbackType })}
            options={TYPES.map((t) => ({ value: t, label: TYPE_LABEL[t] }))}
            hint="Labels the note in the prompt so the agent knows whether to avoid the behaviour or repeat it. It does not change how the note is found."
          />
          <Select
            label="Phase"
            value={form.phaseId}
            onChange={(v) => setForm({ ...form, phaseId: v })}
            options={[
              { value: "", label: "All phases" },
              ...phases.map((p) => ({ value: p.id, label: `${p.name} (${p.slug})` })),
            ]}
            hint="A note tied to a phase is only considered while a prospect sits in it. “All phases” makes it eligible everywhere."
          />
          <TextField
            label="Linked email ID (optional)"
            value={form.relatedEmailId}
            onChange={(v) => setForm({ ...form, relatedEmailId: v })}
            placeholder="UUID of an email this note refers to"
            hint="Provenance only — links the note back to the message that prompted it. It does not narrow when the note applies."
          />
        </div>
        <TextArea
          label="Scenario"
          value={form.scenario}
          onChange={(v) => setForm({ ...form, scenario: v })}
          placeholder="What happened or what pattern are you describing?"
          rows={2}
          required
        />
        <TextArea
          label="Example email (optional)"
          value={form.exampleEmail}
          onChange={(v) => setForm({ ...form, exampleEmail: v })}
          placeholder="Paste the email body that prompted this note, if any."
          rows={3}
        />
        <TextArea
          label="Correction / guideline"
          value={form.correction}
          onChange={(v) => setForm({ ...form, correction: v })}
          placeholder="What should the agent do instead, or repeat?"
          rows={3}
          required
        />
        <div className="flex items-center justify-end gap-3">
          {error ? <span className="text-sm text-[var(--danger)]">{error}</span> : null}
          <button
            type="submit"
            disabled={!submittable || submitting}
            className="rounded-md bg-[var(--primary)] px-4 py-1.5 text-sm font-medium text-white disabled:opacity-50"
          >
            {submitting ? "Saving…" : "Save note"}
          </button>
        </div>
      </form>
    </Card>
  );
}

function FeedbackCard({
  entry,
  canEdit,
  phaseLabel,
  strategyId,
}: {
  entry: FeedbackEntry;
  canEdit: boolean;
  phaseLabel: string;
  strategyId: string;
}) {
  const qc = useQueryClient();

  const setActive = useMutation({
    mutationFn: (active: boolean) =>
      api.patch<FeedbackEntry>(
        `/api/v1/strategies/${strategyId}/feedback/${entry.id}/active`,
        { active },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["feedback", strategyId] }),
  });
  const remove = useMutation({
    mutationFn: () => api.del<void>(`/api/v1/strategies/${strategyId}/feedback/${entry.id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["feedback", strategyId] }),
  });

  return (
    <Card className={cn(!entry.active && "opacity-60")}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={cn(
              "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium",
              TYPE_TONE[entry.type],
            )}
          >
            {TYPE_LABEL[entry.type]}
          </span>
          <span className="text-xs text-[var(--fg-muted)]">Phase: {phaseLabel}</span>
          <span className="text-xs text-[var(--fg-muted)]">
            Added {formatDateTime(entry.createdAt)}
          </span>
          {!entry.active ? (
            <span className="text-xs text-[var(--fg-muted)] italic">inactive</span>
          ) : null}
        </div>
        {canEdit ? (
          <div className="flex items-center gap-2 text-sm">
            <button
              onClick={() => setActive.mutate(!entry.active)}
              className="rounded-md border border-[var(--border)] px-2 py-1 text-xs hover:bg-zinc-50"
            >
              {entry.active ? "Deactivate" : "Reactivate"}
            </button>
            <button
              onClick={() => {
                if (confirm("Delete this training note? This cannot be undone.")) {
                  remove.mutate();
                }
              }}
              className="rounded-md border border-red-200 px-2 py-1 text-xs text-red-700 hover:bg-red-50"
            >
              Delete
            </button>
          </div>
        ) : null}
      </div>

      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
        <div>
          <div className="text-xs uppercase tracking-wide text-[var(--fg-muted)]">Scenario</div>
          <p className="whitespace-pre-wrap text-zinc-800">{entry.scenario}</p>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wide text-[var(--fg-muted)]">
            Correction / guideline
          </div>
          <p className="whitespace-pre-wrap text-zinc-800">{entry.correction}</p>
        </div>
      </div>
      {entry.exampleEmail ? (
        <details className="mt-3 text-sm">
          <summary className="cursor-pointer text-[var(--fg-muted)]">Show example email</summary>
          <pre className="mt-2 whitespace-pre-wrap rounded-md bg-zinc-50 p-3 text-xs text-zinc-800">
            {entry.exampleEmail}
          </pre>
        </details>
      ) : null}
      {entry.relatedEmailId ? (
        <div className="mt-2 text-xs text-[var(--fg-muted)]">
          Linked email: <code className="font-mono">{entry.relatedEmailId}</code>
        </div>
      ) : null}
    </Card>
  );
}

function Select({
  label,
  value,
  onChange,
  options,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  hint?: string;
}) {
  return (
    <label className="text-sm">
      <div className="text-xs text-[var(--fg-muted)] mb-1">{label}</div>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-[var(--border)] bg-white px-3 py-1.5 text-sm"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      {hint ? <p className="mt-1 text-xs text-[var(--fg-muted)]">{hint}</p> : null}
    </label>
  );
}

function TextField({
  label,
  value,
  onChange,
  placeholder,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  hint?: string;
}) {
  return (
    <label className="text-sm">
      <div className="text-xs text-[var(--fg-muted)] mb-1">{label}</div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-md border border-[var(--border)] px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      {hint ? <p className="mt-1 text-xs text-[var(--fg-muted)]">{hint}</p> : null}
    </label>
  );
}

function TextArea({
  label,
  value,
  onChange,
  placeholder,
  rows,
  required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
  required?: boolean;
}) {
  return (
    <label className="text-sm">
      <div className="text-xs text-[var(--fg-muted)] mb-1">
        {label}
        {required ? <span className="text-[var(--danger)]"> *</span> : null}
      </div>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows ?? 3}
        className="w-full rounded-md border border-[var(--border)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    </label>
  );
}
